import React, { useRef, useState } from "react";
import { Button, Field, Input, notify, Select } from "../../../components/ui";
import { useSession } from "../../../context/SessionContext";
import { useAccess } from "../../../hooks/useAccess";
import { useApi } from "../../../hooks/useApi";
import { useEspecialidades } from "../../../hooks/useEspecialidades";
import { t } from "../../../i18n";
import { crmError, formatCrmInput } from "../../../lib/validators";
import { doctorService } from "../../../services/doctors";
import { DoctorUpdateResponse } from "../../../types/doctors.types";

// Nome, CRM e especialidade do médico. Sem acesso completo, só mostra a especialidade atual.
export const ProfileForm: React.FC = () => {
  const { setMetadata, metadata } = useSession();
  const { hasFullAccess } = useAccess();
  const especialidades = useEspecialidades();
  const email = metadata.email;
  const [nome, setNome] = useState(metadata.nome);
  const [crm, setCrm] = useState(metadata.crm);
  const [crmTouched, setCrmTouched] = useState(false);
  const crmRef = useRef<HTMLInputElement>(null);
  const [especialidade, setEspecialidade] = useState(metadata.especialidade);
  const updateApi = useApi<DoctorUpdateResponse>();

  const handleSave = async () => {
    if (!email) return;
    if (!crm.trim() || crmError(crm)) {
      setCrmTouched(true);
      crmRef.current?.focus();
      return;
    }
    if (nome.length < 6) return notify({ tone: "error", title: t("settings.profile.nameTooShort") });

    const result = await updateApi.request(
      () => doctorService.update(updateApi.apiFetch, email, { nome, email, crm, especialidade }),
      { successMessage: t("settings.profile.saved") },
    );
    if (!result) return;

    setMetadata((prev) => ({ ...prev, nome: result.nome, crm: result.crm, especialidade: result.especialidade }));
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label={t("settings.profile.name")}>
          <Input placeholder={t("settings.profile.namePlaceholder")} value={nome} onChange={(e) => setNome(e.target.value)} />
        </Field>
        <Field
          label={t("settings.profile.crm")}
          hint={t("settings.profile.crmHint")}
          error={crmTouched ? crmError(crm) ?? (crm.trim() ? null : t("validation.crmFormat")) : null}
        >
          <Input
            ref={crmRef}
            placeholder="123456/SP"
            value={crm}
            onChange={(e) => setCrm(formatCrmInput(e.target.value))}
            onBlur={() => setCrmTouched(true)}
          />
        </Field>
      </div>
      <Field label={t("settings.profile.specialty")}>
        <Select value={especialidade} onChange={(e) => setEspecialidade(e.target.value)}>
          <option value="">{t("settings.profile.specialtyPlaceholder")}</option>
          {especialidades
            .filter((e) => hasFullAccess || e.chave === especialidade)
            .map((e) => (
              <option key={e.chave} value={e.chave}>{e.nome}</option>
            ))}
        </Select>
      </Field>
      <Button variant="primary" className="self-start" onClick={handleSave} disabled={updateApi.loading || !email}>
        {updateApi.loading ? t("settings.profile.saving") : t("settings.profile.save")}
      </Button>
    </div>
  );
};
